// PMS-Dashboard/src/App.js
import React, { useState, useEffect, createContext, useContext } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged
} from 'firebase/auth';
import { auth, db } from './firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';

// Import your components
import AuthPage from './components/AuthPage';
import InventoryDashboard from './components/InventoryDashboard';
import AdminPage from './components/AdminPage';
import MainRouterPage from './components/MainRouterPage';

// --- PLACE THESE STYLE DEFINITIONS HERE, RIGHT AFTER IMPORTS ---
const appStyles = {
    fontFamily: 'Arial, sans-serif',
    maxWidth: '800px',
    margin: '20px auto',
    padding: '20px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

const messageStyles = {
    padding: '20px',
    margin: '20px auto',
    backgroundColor: '#fff3cd',
    color: '#856404',
    border: '1px solid #ffeeba',
    borderRadius: '8px',
    textAlign: 'center',
};

const deniedMessageStyles = {
    ...messageStyles,
    backgroundColor: '#f8d7da',
    color: '#721c24',
    borderColor: '#f5c6cb',
};
// --- END OF STYLE DEFINITIONS ---

export const AuthContext = createContext(null);

function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [approvalStatus, setApprovalStatus] = useState('unknown');
    const [isAdmin, setIsAdmin] = useState(false);
    const [userRoles, setUserRoles] = useState([]);

    const ADMIN_EMAIL = 'vernannekanico@gmail.com'; // <--- !!! ENSURE THIS IS YOUR ACTUAL ADMIN EMAIL !!!

    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, async user => {
            setCurrentUser(user);

            if (user) {
                const personnelRef = doc(db, 'personnel', user.uid);
                const unsubscribePersonnel = onSnapshot(personnelRef, (docSnap) => {
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        const currentStatus = data.status || 'pending_approval';
                        const currentRoles = data.roles || [];

                        setApprovalStatus(currentStatus);
                        setUserRoles(currentRoles);
                        setIsAdmin(currentRoles.includes('admin') || user.email === ADMIN_EMAIL);
                    } else {
                        setApprovalStatus('pending_approval');
                        setUserRoles(['pending']);
                        setIsAdmin(false);
                    }
                    setLoading(false);
                }, (error) => {
                    console.error("Error fetching personnel approval status or roles:", error);
                    setApprovalStatus('error');
                    setUserRoles([]);
                    setIsAdmin(false);
                    setLoading(false);
                });

                return () => {
                    unsubscribeAuth();
                    unsubscribePersonnel();
                };
            } else {
                // --- DEBUG LOGS START ---
                console.log("AuthProvider useEffect: No user authenticated.");
                // --- DEBUG LOGS END ---
                setApprovalStatus('not_authenticated');
                setUserRoles([]);
                setIsAdmin(false);
                setLoading(false);
                 return () => {
                    unsubscribeAuth();
                };
            }
        });
        return unsubscribeAuth;
    }, []);

    const value = {
        currentUser,
        loading,
        approvalStatus,
        isAdmin,
        userRoles,
        signup: (email, password) => createUserWithEmailAndPassword(auth, email, password),
        login: (email, password) => signInWithEmailAndPassword(auth, email, password),
        logout: () => signOut(auth),
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}

// --- 4. Main App Component ---
function App() {
    const { currentUser, loading, approvalStatus, isAdmin, logout, userRoles } = useContext(AuthContext);

    if (loading) {
        return <div style={appStyles}>Loading application...</div>;
    }

    if (currentUser && isAdmin) {
        console.log("  - Rendering AdminPage.");
        return <AdminPage />;
    }

    if (currentUser && approvalStatus === 'denied') {
        console.log("  - Rendering 'Access Denied' page (status: denied).");
        return (
            <div style={appStyles}>
                <div style={deniedMessageStyles}>
                    <h3>Access Denied</h3>
                    <p>Your account has been denied access to the inventory system. Please contact the administrator for further information.</p>
                    <button onClick={logout} style={{ marginTop: '15px', padding: '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Log Out</button>
                </div>
            </div>
        );
    }

    if (currentUser && approvalStatus === 'pending_approval') {
        return (
            <div style={appStyles}>
                <div style={messageStyles}>
                    <h3>Access Pending Approval</h3>
                    <p>Your registration is awaiting approval from an administrator. You will gain access once your account is activated.</p>
                    <button onClick={logout} style={{ marginTop: '15px', padding: '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Log Out</button>
                </div>
            </div>
        );
    }

    if (currentUser && approvalStatus === 'approved') {
        return <MainRouterPage />;
    }


    // Default: If not logged in or any other state, show AuthPage
    return <AuthPage />;
}

// --- 5. Export App wrapped in AuthProvider ---
export default function WrappedApp() {
    return (
        <AuthProvider>
            <App />
        </AuthProvider>
    );
}
